import { getTestAttributes, noop } from '@chillibean/component-library';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

function Accordion(props) {
  const {
    accordionIndex = undefined,
    activeAccordionIndex = undefined,
    children,
    customFunction = noop,
    disable = false,
    displayHeaderOnOpen = false,
    helperText = undefined,
    icon = {},
    iconText = undefined,
    title = undefined,
  } = props;

  // HOOKS
  // React
  const expanded = useMemo(() => accordionIndex === activeAccordionIndex, [activeAccordionIndex]);
  const contentRef = useRef();
  const [contentHeight, setContentHeight] = useState(0);

  // METHODS
  const toggleAccordion = () => {
    customFunction();
  };

  // EFFECTS
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  useEffect(() => {
    let timeout;

    if (expanded) {
      timeout = setTimeout(() => contentRef.current.classList.add('accordion__content--allow-overflow'), 600);
    } else {
      contentRef.current.classList.remove('accordion__content--allow-overflow');
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [expanded]);

  useEffect(() => {
    const onResize = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="accordion">
      <div className="accordion__header">
        <span className="accordion__title--small">{helperText}</span>
        <span className="accordion__title">{title}</span>
        <span className="accordion__icon-text">{iconText}</span>
        <i
          className={clsx({
            'svg-icon': icon.isSVG,
            [`fa ${icon.icon}`]: !icon.isSVG,
          })}
          aria-hidden="true"
          onClick={disable ? null : toggleAccordion}
          {...getTestAttributes('accordion-toggle')}
        >
          {icon.isSVG && (
            <span
              className={clsx('accordion__icon', {
                'accordion__icon-rotate': accordionIndex === activeAccordionIndex,
              })}
            >
              {icon.icon(28, 28)}
            </span>
          )}
        </i>
        {React.Children.count(children) === 2 && (displayHeaderOnOpen || accordionIndex !== activeAccordionIndex) && (
          <AccordionContent className="accordion__header--toggle" onClick={toggleAccordion}>
            {children[0]}
          </AccordionContent>
        )}
      </div>

      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? `${contentHeight}px` : undefined,
        }}
        className="accordion__content"
      >
        {React.Children.count(children) === 1 ? children : children[1]}
      </div>
    </div>
  );
}

const AccordionContent = styled.div``;

export function AccordionToggleHeader(props) {
  const { children } = props;

  return children;
}

AccordionToggleHeader.propTypes = {
  children: PropTypes.node.isRequired,
};

export function AccordionCollapse(props) {
  const { children } = props;

  return children;
}

AccordionCollapse.propTypes = {
  children: PropTypes.node.isRequired,
};

Accordion.propTypes = {
  accordionIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activeAccordionIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  customFunction: PropTypes.func,
  disable: PropTypes.bool,
  displayHeaderOnOpen: PropTypes.bool,
  helperText: PropTypes.string,
  icon: PropTypes.shape({
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    isSVG: PropTypes.bool,
  }),
  iconText: PropTypes.string,
  title: PropTypes.string,
};

export default Accordion;
